import React from "react";
import {
  ShieldCheck, ClipboardText, ArrowRight, NetworkX, FloppyDiskBack, MicrosoftOutlookLogo, WifiHigh,
} from "@phosphor-icons/react";

const ASSESSMENT_AREAS = [
  { icon: NetworkX,                title: "Network Health Review",        desc: "Switches, routing, VLANs, bandwidth, and bottlenecks" },
  { icon: ShieldCheck,             title: "Cybersecurity Risk Check",     desc: "Endpoint protection, MFA, phishing exposure, dark-web exposure" },
  { icon: MicrosoftOutlookLogo,    title: "Microsoft 365 Review",         desc: "License waste, security baselines, sharing & compliance risk" },
  { icon: FloppyDiskBack,          title: "Backup Verification",          desc: "Backup integrity, RPO/RTO, ransomware exposure, recovery drills" },
  { icon: WifiHigh,                title: "Wi-Fi Performance Review",     desc: "Speed, dead zones, guest network isolation, AP placement" },
  { icon: ClipboardText,           title: "Priority Improvement Roadmap", desc: "Risk-ranked, prioritized action plan with cost estimates" },
];

const DELIVERABLES = [
  "Written executive summary with prioritized findings",
  "Risk-ranked action plan (quick wins + 90-day roadmap)",
  "Per-user cost estimate to address each gap",
  "30-minute review call with the technician who scoped it",
];

export default function BusinessAssessment({ onBook }) {
  return (
    <section
      id="business-assessment"
      data-testid="business-assessment-section"
      className="bg-white py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-start">
          {/* Left: pitch */}
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest">
              <ClipboardText size={14} weight="fill" /> Complimentary · 30-min on-site or remote
            </div>

            <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900">
              Free 30-Minute Business Technology Assessment
            </h2>

            <p className="mt-5 text-lg text-zinc-600 leading-relaxed">
              We&rsquo;ll evaluate your network, cybersecurity, backups, Microsoft 365 environment,
              and Wi-Fi — then provide a customized improvement plan with prioritized next steps
              and clear cost estimates. No obligation. No sales pressure.
            </p>

            <div className="mt-8">
              <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">
                What we deliver
              </div>
              <ul className="space-y-2.5">
                {DELIVERABLES.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-zinc-700">
                    <span className="mt-2 h-1.5 w-1.5 rounded-full bg-blue-600 shrink-0" />
                    <span className="text-[15px]">{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() => onBook && onBook("Free 30-Minute Business Technology Assessment")}
                data-testid="assessment-book-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3.5 font-semibold blue-glow transition-all"
              >
                Schedule your free assessment
                <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="#contact"
                data-testid="assessment-contact-btn"
                className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-900 px-6 py-3.5 font-semibold transition-all"
              >
                Request a proposal
              </a>
            </div>

            <p className="mt-5 text-xs text-zinc-500">
              30-minute assessment delivered on-site or remotely. Written plan within 3 business days.
            </p>
          </div>

          {/* Right: areas evaluated */}
          <div className="bg-zinc-950 text-white rounded-2xl p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute inset-0 hero-grid opacity-30" />
            <div className="relative">
              <div className="text-xs font-semibold uppercase tracking-widest text-blue-300 mb-4">
                Areas evaluated
              </div>
              <div className="space-y-4">
                {ASSESSMENT_AREAS.map((a, i) => {
                  const Icon = a.icon;
                  return (
                    <div
                      key={a.title}
                      data-testid={`assessment-area-${i}`}
                      className="flex items-start gap-4 p-3 rounded-xl hover:bg-white/5 transition-colors"
                    >
                      <div className="h-10 w-10 shrink-0 rounded-lg bg-blue-600/25 text-blue-300 grid place-items-center">
                        <Icon size={20} weight="duotone" />
                      </div>
                      <div>
                        <div className="font-semibold text-white">{a.title}</div>
                        <div className="text-sm text-zinc-400 leading-snug mt-0.5">{a.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-3 text-sm text-zinc-300">
                <ShieldCheck size={18} weight="duotone" className="text-blue-400" />
                NDA available · Your data and findings stay confidential.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
